import { type TaskConflict } from 'src/services/jmr/types';
import { MeasureUnitValues } from 'src/services/tasks/constants';
import { setJmrResults } from 'src/store/newJmrPageSlice/newJmrPageSlice';
import { type BackgroundFormData, type JmrTask } from 'src/store/newJmrPageSlice/types';
import { type AppDispatch } from 'src/store/store';
import {
  canJobBeModified,
  ConflictColorCode,
  type FlatTableDataRow,
  hasJobBeenModifiedForAllConflicts,
  type TableDataRow,
} from 'src/utils/jmrUtils';

/**
 * Is a job valid (can be saved)
 */
export const getJobError = (
  job: TableDataRow | FlatTableDataRow,
  allJobs: (TableDataRow | FlatTableDataRow)[],
  backgroundFormData: BackgroundFormData,
  jobsExceedingRestrictions: Set<string>,
) => {
  const isJobWithinCapacity =
    !job?.jobCapacity /*if job doesn't have capacity data, its always within capacity*/ ||
    (job.jobCapacity && job?.jobCapacity >= +job.totalHours); /*check if within capacity*/

  const areTaskHoursWithinRestrictedValues = job.conflicts
    // preventing issues that may arise from Yes/No restrictedValues
    .filter((conflict) => +conflict.restrictedValue > 0)
    .every(
      (conflict) =>
        conflict.appliedModifications?.length /*if modified, a conflict is always within restrictions */ ||
        getRequiredTaskHours(job, conflict) <= +conflict.restrictedValue /*check if hours are within restrictions*/,
    );

  const totalSelectedHours = allJobs.filter((r) => r.isChecked).reduce((acc, row) => acc + (+row?.totalHours || 0), 0);

  const actualShiftLength = backgroundFormData.cannotWorkNormalShiftHrs
    ? +backgroundFormData.limitedHoursPerDay!
    : backgroundFormData.shiftLength;

  if (isNaN(+job.totalHours) || +job.totalHours <= 0) {
    return 'Must be a number greater than zero.';
  } else if (totalSelectedHours > actualShiftLength) {
    return 'Allocated hours exceed shift length';
  } else if (!areTaskHoursWithinRestrictedValues) {
    return 'Allocated hours exceed restrictions';
  } else if (jobsExceedingRestrictions.has(job.id)) {
    return 'Allocated hours across multiple jobs exceed restrictions';
  } else if (!isJobWithinCapacity) {
    return 'Allocated hours exceed capacity';
  }

  return null;
};

/**
 * get type of conflict (used to color code and perform placement logic)
 */
export const getConflictsType = (jobMatch: TableDataRow) => {
  if (jobMatch.tierTwoPlusJobModified && !jobMatch?.modificationNotes?.trim().length) {
    return ConflictColorCode.UNSAFE_CONFLICTS_BUT_MODIFICATIONS_AVAILABLE;
  }

  if (jobMatch.perfectMatch || hasJobBeenModifiedForAllConflicts(jobMatch.conflicts)) {
    return ConflictColorCode.NO_CONFLICTS;
  } else if (!jobMatch.conflicts?.length) {
    return ConflictColorCode.IMPERFECT_CONFLICTS;
  } else if (jobMatch.maximumHoursAssociateCanDo > 0) {
    return ConflictColorCode.SAFE_CONFLICTS;
  } else {
    return canJobBeModified(jobMatch.conflicts, jobMatch.shifts, jobMatch.jobCapacity)
      ? ConflictColorCode.UNSAFE_CONFLICTS_BUT_MODIFICATIONS_AVAILABLE
      : ConflictColorCode.UNSAFE_CONFLICTS_AND_NO_MODIFICATIONS_AVAILABLE;
  }
};

/** Whether associate can work full shift */
export const areConflictsResolved = (conflicts: TaskConflict[]) => {
  return !conflicts?.length || conflicts.every((c) => c.appliedModifications?.length);
};

/**
 * Check or uncheck a job placement and return a new job results array.
 * For multi-shift jobs, unchecking the job unchecks all shifts.
 */
export const toggleJob = (id: string, jmrResults: TableDataRow[], dispatch: AppDispatch) => {
  const newJmrResults = jmrResults.map((r) => {
    if (r.id !== id) return r;
    const newIsChecked = !r.isChecked;
    if (r.shifts.length > 1) {
      const newShifts = r.shifts.map((s) => ({
        ...s,
        isChecked: newIsChecked ? s.isChecked : false,
        totalHours: newIsChecked ? s.totalHours : '',
      }));
      return { ...r, isChecked: newIsChecked, totalHours: newIsChecked ? r.totalHours : '', shifts: newShifts };
    }
    return {
      ...r,
      isChecked: newIsChecked,
      totalHours: r.isChecked ? '' : r.totalHours,
    } as TableDataRow;
  });
  dispatch(setJmrResults(newJmrResults));
  return newJmrResults;
};

/**
 *  Calculate the required hours for a given task in a single placement row (legacy)
 * NOTE: this method is dependent on a call to getJob when the job is selected which populates the row's jobRequirements.
 *  Said call is only made in the pre-modifications (legacy) experience
 */
export const calculatePlacementHours = (row: TableDataRow | FlatTableDataRow, task: JmrTask) => {
  // get the percentage from the job requirements
  const requirmentValues = row.jobRequirements?.filter((t) => t.task.id === task.taskId) ?? [];
  // multiply by the assigned hours of each job
  const assignedValue = requirmentValues?.length > 0 ? +(+requirmentValues[0].value * +row.totalHours).toFixed(1) : 0;
  return assignedValue;
};

/**
 *  Calculate the total required hours for a given task across all placements (legacy)
 * NOTE: this method is dependent on a call to getJob when the job is selected which populates the row's jobRequirements.
 *  Said call is only made in the pre-modifications (legacy) experience
 */
export const calculateTotalHours = (task: JmrTask, selectedResults: (TableDataRow | FlatTableDataRow)[]) => {
  return selectedResults.length > 0
    ? +selectedResults
        .map((r) => calculatePlacementHours(r, task))
        .reduce((prev, curr) => prev + curr)
        .toFixed(1)
    : 0;
};

/**
 *  Calculate the unused hours in a task
 */
export const calculateUnusedHoursForTask = (task: JmrTask, selectedResults: (TableDataRow | FlatTableDataRow)[]) => {
  return +(+task.restrictedValue - calculateTotalHours(task, selectedResults)).toFixed(1);
};

export const getModifiedShiftLength = (shiftLength: number, limitedHoursPerDay?: number | string) =>
  limitedHoursPerDay ? +limitedHoursPerDay : shiftLength;

export const areAllModifiedT2JobsValid = (selectedJobs: (TableDataRow | FlatTableDataRow)[]) =>
  selectedJobs.filter((job) => job.tierTwoPlusJobModified).every((job) => job.modificationNotes?.trim().length > 0);

/**
 *  Calculate unused total hours for a table
 */
export const calculateUnusedTotalHours = (
  selectedResults: (TableDataRow | FlatTableDataRow)[],
  shiftLength: number,
  // TODO limitedHoursPerDay should be string | undefined and converted to number in this method instead of during call
  limitedHoursPerDay: number,
) => {
  const totalHoursSum =
    selectedResults.length > 0 ? selectedResults.map((r) => +r.totalHours).reduce((prev, curr) => prev + curr) : 0;
  return getModifiedShiftLength(shiftLength, limitedHoursPerDay) - totalHoursSum;
};

/**
 *  Validate total unused hours based on the contraints in background page
 */
// TODO limitedHoursPerDay should be string | undefined and converted to number in this method instead of during call
export const isTotalUnusedHoursValid = (totalUnusedHours: number, shiftLength: number, limitedHoursPerDay?: number) => {
  const totalCap = getModifiedShiftLength(shiftLength, limitedHoursPerDay);
  return totalUnusedHours >= 0 && totalUnusedHours < totalCap;
};

export const areAllUsedHoursMoreThanZero = (selectedJobs: (TableDataRow | FlatTableDataRow)[]) => {
  return selectedJobs.every((selectedJob) => +selectedJob.totalHours > 0);
};

/** Returns the number of hours a given conflicting requirement would need to be performed base on the hours allocated to
 * the job. NOTE this function is meant for checking thresholds, values are not suitable for display, caller should use toFixed */
export function getRequiredTaskHours(
  jobMatch: TableDataRow | FlatTableDataRow,
  conflict: TaskConflict,
  shiftLength?: number,
) {
  if (+jobMatch.totalHours) {
    return +conflict.requirementProportion * +jobMatch.totalHours;
  }
  if (shiftLength) {
    return +conflict.requirementProportion * shiftLength;
  }
  return 0;
}

export function getRequiredTaskPercent(conflict: TaskConflict) {
  return +conflict.requirementProportion * 100;
}

/** Returns a string outlining the amount of a shift the conflict task is required for (in hours if any are allocated) */
export function getRequirementQuantity(
  jobMatch: TableDataRow | FlatTableDataRow,
  conflict: TaskConflict,
  shiftLength?: number,
) {
  const taskHours = getRequiredTaskHours(jobMatch, conflict, shiftLength);
  return taskHours ? `${taskHours.toFixed(1)} hours` : `${getRequiredTaskPercent(conflict).toFixed(1)}%`;
}

function getDefaultTaskAccumulator(conflict: TaskConflict) {
  const { taskId, taskName } = conflict;
  const contributingJobs: { id: string; name: string }[] = []; // setup type hint for ReturnType<>
  const restrictedHours = conflict.measureUnitType != MeasureUnitValues.Bool ? +conflict.restrictedValue : 0;

  return {
    taskId,
    taskName,
    restrictedHours,
    totalHours: 0,
    contributingJobs,
  };
}
// Map.get always has `any` type, use this definition to fix that
export type TaskTotalAndJobs = ReturnType<typeof getDefaultTaskAccumulator>;

export function getRequirementSumsAndJobs(selectedJobs: (TableDataRow | FlatTableDataRow)[]) {
  const accumulatedMap = selectedJobs.reduce((accumulator, sj) => {
    if (!sj.conflicts?.length) {
      return accumulator;
    } // exclude jobs with no conflicts

    return sj.conflicts.reduce((accumulator, c) => {
      if (c.appliedModifications?.length) {
        return accumulator;
      } // exclude modified conflicts

      if (!c.requirementProportion) {
        console.warn(
          "Job '%s' task '%s' is missing requirementProportion. The job may be misconfigured or there may be a problem in fetching this information.",
          sj.name,
          c.taskName,
        );
        return accumulator;
      }

      if (!accumulator.has(c.taskId)) {
        accumulator.set(c.taskId, getDefaultTaskAccumulator(c)); // setup initial task accumulator
      }
      const taskLevelAccumulator = accumulator.get(c.taskId)!; // always set per above
      // Update by reference
      const requiredTaskHours = getRequiredTaskHours(sj, c);
      if (requiredTaskHours) {
        taskLevelAccumulator.totalHours += requiredTaskHours;
        const { id, name } = sj;
        taskLevelAccumulator.contributingJobs.push({ id, name });
      }

      return accumulator;
    }, accumulator);
  }, new Map<number, TaskTotalAndJobs>());

  return Array.from(accumulatedMap.values());
}

export const getRequirementColor = (
  { taskId, appliedModifications }: TaskConflict,
  exceededRestrictionIds: Set<number>,
) => (!appliedModifications?.length && exceededRestrictionIds.has(taskId) ? 'error' : undefined);
